import { useMutation } from '@tanstack/react-query';
import { stringify } from 'query-string';
import { endOfDay, startOfDay } from 'date-fns';
import React from 'react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import downloadUrlAsFile from '@dashboard/services/downloadUrlAsFile';
import { fetcher } from '@dashboard/services/request';
import Features from '@dashboard/enums/features';

import DateRangePicker from './Form/components/DateRangePicker';
import Label from './Form/components/Label';
import Spinner from './Spinner';
import { FormElement } from './Form';
import { FormErrorBase } from './Formik/components/FormError';
import CanUseFeature from './CanUseFeature';
import Link from './Link';
import Dialog from './Dialog';
import { Button } from './ui/button';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const states = {
  IDLE: 'idle',
  EXPORTING: 'exporting',
  DOWNLOADING: 'downloading',
  DONE: 'done',
};

const ExportView = ({ collection, label, filters }) => {
  const { sellerId, token } = useAuth();
  const [state, setState] = React.useState(states.IDLE);
  const [dateRange, setDateRange] = React.useState(null);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [exportUrl, setExportUrl] = React.useState(null);
  const [exportFilename, setExportFilename] = React.useState(null);
  const [error, setError] = React.useState(null);

  const exportMutation = useMutation({
    mutationFn: () =>
      fetcher(
        `/sellers/${sellerId}/exports/${collection}?${stringify({
          ...filters,
          startDate,
          endDate,
        })}`,
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: data => {
      setState(states.DOWNLOADING);
      downloadExport(data._id);
    },
    onMutate: () => {
      setError(null);
      setState(states.EXPORTING);
    },
    onError: error => {
      setState(states.IDLE);
      setError(error.message);
    },
  });

  async function downloadExport(exportId) {
    const data = await fetcher(`/sellers/${sellerId}/exports/${exportId}/download`, token);

    setExportUrl(data.url);
    setExportFilename(data.filename);

    downloadUrlAsFile(data.url, data.filename);

    setState(states.DONE);
  }

  function handleClose() {
    setState(states.IDLE);
    setExportUrl(null);
    setExportFilename(null);
    setError(null);
  }

  return (
    <>
      <CanUseFeature feature={Features.EXPORTS} showIcon={false} inline>
        <Dialog
          title={`Export ${label}`}
          cancelButton={
            state !== states.DONE
              ? {
                  label: 'Cancel',
                  appearance: 'link',
                }
              : {
                  label: 'Close',
                  appearance: 'primary',
                }
          }
          confirmButton={
            state === states.IDLE && {
              label: 'Export',
              appearance: 'primary',
              disabled: state !== states.IDLE,
              onClick: () => exportMutation.mutate(),
            }
          }
          onCancel={handleClose}
          trigger={
            <Button variant="outline">
              Export
              <ArrowDownTrayIcon className="w-4 h-4" />
            </Button>
          }
          size="lg"
        >
          {state === states.IDLE && (
            <>
              <FormElement>
                <Label className="mb-2">Date range</Label>

                <DateRangePicker
                  dateRange={dateRange}
                  onChange={({ startDate, endDate, dateRange }) => {
                    setDateRange(dateRange);

                    if (startDate) {
                      setStartDate(startOfDay(startDate));
                    } else {
                      setStartDate(null);
                    }

                    if (endDate) {
                      setEndDate(endOfDay(endDate));
                    } else {
                      setEndDate(null);
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                />
              </FormElement>
            </>
          )}

          {state === states.EXPORTING && (
            <div className="flex flex-col items-center justify-center text-gray-800 dark:text-foreground">
              <Spinner />

              <p className="mt-4">Exporting {label}...</p>
            </div>
          )}

          {state === states.DOWNLOADING && (
            <div className="flex flex-col items-center justify-center text-gray-800 dark:text-foreground">
              <Spinner />

              <p className="mt-4">Starting download...</p>
            </div>
          )}

          {state === states.DONE && (
            <>
              <p>
                {`Your export is ready. If you don't see your file,`}{' '}
                <Link href={exportUrl} download={exportFilename} variant="secondary">
                  you can try downloading it again
                </Link>
                .
              </p>
            </>
          )}

          {error && (
            <FormElement>
              <FormErrorBase>{error}</FormErrorBase>
            </FormElement>
          )}
        </Dialog>
      </CanUseFeature>
    </>
  );
};

export default ExportView;
